<template>
  <div class="field">
    <label class="label" v-if="props.etichetta">{{ props.etichetta }}</label>
    <div class="field has-addons">
      <div class="control is-expanded">
        <input 
          class="input has-text-centered" 
          type="text" 
          v-model="valoreCondizionatoTesto" 
          placeholder="GG/MM/AAAA"
          maxlength="10"
          :title="props.titolo"
          @input="formattaTestoData"
          @blur="modificaValoreTesto"
          @keyup.enter="modificaValoreTesto">
      </div>

      <div class="control" :title="props.titolo">
        <div class="button is-primary picker-container">
          <span class="icon is-small">
            &nbsp;&nbsp;&nbsp;<i class="fas fa-calendar-alt"></i>
          </span>
          <input 
            class="input-data-nascosto" 
            type="date" 
            v-model="valoreCondizionatoCalendario"
            :title="props.titolo"
            @change="modificaValoreCalendario">
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
    import { ref, defineProps, defineEmits, watch, nextTick } from 'vue';
    import { Timestamp } from 'firebase/firestore';
    import { dataDaTimestamp } from '../code/utilita';

    const props = defineProps({
        valore: {
            type: Object,
            default: null
        },
        identificativo: {
            type: String,
            default: ''
        },
        etichetta: {
            type: String,
            default: ''
        },
        titolo: {
            type: String,
            default: ''
        }
    });
    const emit = defineEmits(['valoreCambiato']);

    // VARIABILI DEL COMPONENTE
    const valoreCondizionatoTesto = ref(dataDaTimestamp(props.valore, 'testo'));
    const valoreCondizionatoCalendario = ref(dataDaTimestamp(props.valore));
    let inAttesaDiModifica = false;     // Inibisce ulteriori modifiche se è stato lanciato un emit

    // SETUP DEL COMPONENTE
    // FIX: Sincronizzazione in tempo reale con lo stato di Firestore/Parent
    watch(() => props.valore, (nuovoValore) => {
        valoreCondizionatoTesto.value = dataDaTimestamp(nuovoValore, 'testo');
        valoreCondizionatoCalendario.value = dataDaTimestamp(nuovoValore);
    });

    // FUNZIONI DEL COMPONENTE
    async function modificaValore() {
        let tempValore = null;
        if (typeof(valoreCondizionatoCalendario.value)=='string') {
            if (valoreCondizionatoCalendario.value != dataDaTimestamp(props.valore)) {
                // La data è cambiata

                if (valoreCondizionatoCalendario.value == '') {
                    if (!inAttesaDiModifica) {
                        inAttesaDiModifica = true;
                        emit('valoreCambiato', null, props.identificativo);
                        
                        // FIX: Sblocco asincrono per permettere la ricezione del click nativo (calendario)
                        await nextTick();
                        inAttesaDiModifica = false;
                    }
                } else {
                    try {
                        tempValore = Timestamp.fromDate(new Date(valoreCondizionatoCalendario.value));
                        // data limite: 01/01/1950
                        const dataMinima = new Date(1950, 0, 1); // mese 0 = gennaio
                        if (tempValore.toDate() > dataMinima) {
                            if (!inAttesaDiModifica) {
                                inAttesaDiModifica = true;
                                emit('valoreCambiato', tempValore, props.identificativo);
                                
                                // FIX: Sblocco asincrono
                                await nextTick();
                                inAttesaDiModifica = false;
                            }
                        }
                    } catch (error) {
                        console.log('Data errata');
                    }
                }
            }
        }
    }

    function modificaValoreCalendario() {
        valoreCondizionatoTesto.value = formattaDataCalendarioDaTesto(valoreCondizionatoCalendario.value);
        modificaValore();
    }

    function modificaValoreTesto() {
        valoreCondizionatoCalendario.value = formattaDataTestoDaCalendario(valoreCondizionatoTesto.value);
        modificaValore();
    }

    function formattaDataCalendarioDaTesto(data) {
        // Converte una data dal formato AAAA-MM-GG al formato GG/MM/AAAA
        if (data) {
            let temp = data.split('-');
            if (temp.length==3) {
                return temp[2] + '/' + temp[1] + '/' + temp[0];
            }
        }
        return '';
    }

    function formattaDataTestoDaCalendario(data) {
        // Converte una data dal formato GG/MM/AAAA al formato AAAA-MM-GG
        if (data) {
            let temp = data.split('/');
            if (temp.length==3) {
                return temp[2] + '-' + temp[1] + '-' + temp[0];
            }
        }
        return '';
    }

    function formattaTestoData(event) {
        // Se l'utente sta cancellando, non aggiungiamo la barra
        if (event.inputType === 'deleteContentBackward') return;

        let v = valoreCondizionatoTesto.value.replace(/\D/g, ''); // Rimuove tutto ciò che non è un numero
        if (v.length >= 2 && v.length < 4) {
            valoreCondizionatoTesto.value = `${v.slice(0, 2)}/${v.slice(2)}`;
        } else if (v.length == 4) {
            valoreCondizionatoTesto.value = `${v.slice(0, 2)}/${v.slice(2)}/`;
        } else if (v.length >= 4) {
            valoreCondizionatoTesto.value = `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4, 8)}`;
        }
    }

</script>

<style scoped>
.picker-container {
  position: relative;
  overflow: hidden;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.input-data-nascosto {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 10;
}

.input-data-nascosto::-webkit-calendar-picker-indicator {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    cursor: pointer;
}
</style>